import { useCallback, useMemo, useRef, useState } from 'react';
import type { Card, CardResult } from '../../../shared/types';

/** How many times one card may come back around in a single session. */
const MAX_REQUEUES = 2;

export interface SessionOptions {
  /** Put a card you got wrong back into this same session, later on. */
  repeatMissed: boolean;
}

/** The queue and the number of cards it was dealt with, kept in lockstep. */
interface Dealt {
  queue: Card[];
  total: number;
}

export interface SessionState {
  /** The card on screen, or null once the session is over. */
  current: Card | null;
  flipped: boolean;
  /** Cards answered so far, counting repeats. */
  answered: number;
  /** Cards still queued, including repeats already scheduled. */
  remaining: number;
  /** Distinct cards dealt this session — the denominator people expect. */
  total: number;
  correct: number;
  incorrect: number;
  done: boolean;
  /** Every answer, in order, ready to POST. Repeats appear more than once. */
  results: { cardId: string; result: CardResult }[];
  /** Distinct cards that ended the session on a wrong answer. */
  missedCards: Card[];
  flip: () => void;
  answer: (result: CardResult) => void;
  /** Deals a fresh session from the same deck. */
  restart: (cards?: Card[]) => void;
}

/**
 * Runs one pass over exactly the cards it is given.
 *
 * Choosing *which* cards — shuffling the deck, cutting it into sittings,
 * tracking how far through a long section you are — belongs to the caller, not
 * here. Keeping that out means this hook has one job, and it means a session
 * can be handed the next twenty cards of a run, or just the ones you missed,
 * without knowing the difference.
 *
 * The session is held entirely in the browser and posted once at the end, so a
 * drill never waits on the network between cards. The cost is that abandoning a
 * session loses it, which is why long decks are cut into sittings.
 */
export function useFlashcardSession(cards: Card[], options: SessionOptions): SessionState {
  // The queue and the size it started at are one piece of state, dealt once.
  // Held together because they must agree: dealing them separately means two
  // independent shuffles, and `total` describing a hand that was never played.
  const [dealt, setDealt] = useState<Dealt>(() => ({ queue: [...cards], total: cards.length }));
  const { queue, total } = dealt;

  const [flipped, setFlipped] = useState(false);
  const [results, setResults] = useState<{ cardId: string; result: CardResult }[]>([]);

  // How many times each card has been sent back for another go this session.
  const requeues = useRef<Record<string, number>>({});
  // The last answer for each card, so the summary can list what is still shaky.
  const lastResult = useRef<Record<string, CardResult>>({});
  const seenCards = useRef<Record<string, Card>>({});

  const current = queue[0] ?? null;

  const flip = useCallback(() => setFlipped(true), []);

  const answer = useCallback(
    (result: CardResult) => {
      const card = queue[0];
      if (!card) return;

      setResults((prev) => [...prev, { cardId: card.id, result }]);
      lastResult.current[card.id] = result;
      seenCards.current[card.id] = card;

      setDealt((prev) => {
        const rest = prev.queue.slice(1);
        if (result === 'correct' || !options.repeatMissed) {
          return { ...prev, queue: rest };
        }

        // A missed card goes to the back rather than straight back in front —
        // answering it again immediately tests short-term memory and nothing
        // else. The cap stops one stubborn card from making the session endless.
        const seen = requeues.current[card.id] ?? 0;
        if (seen >= MAX_REQUEUES) return { ...prev, queue: rest };
        requeues.current[card.id] = seen + 1;
        return { ...prev, queue: [...rest, card] };
      });

      setFlipped(false);
    },
    [queue, options.repeatMissed],
  );

  const restart = useCallback(
    (nextCards?: Card[]) => {
      const fresh = [...(nextCards ?? cards)];
      requeues.current = {};
      lastResult.current = {};
      seenCards.current = {};
      setDealt({ queue: fresh, total: fresh.length });
      setResults([]);
      setFlipped(false);
    },
    [cards],
  );

  const { correct, incorrect } = useMemo(() => {
    // Counted over distinct cards by their final answer, not over every
    // keypress: a card missed and then recovered reads as learned, which is
    // what actually happened.
    let right = 0;
    let wrong = 0;
    for (const result of Object.values(lastResult.current)) {
      if (result === 'correct') right += 1;
      else wrong += 1;
    }
    return { correct: right, incorrect: wrong };
  }, [results]);

  const missedCards = useMemo(
    () =>
      Object.entries(lastResult.current)
        .filter(([, result]) => result === 'incorrect')
        .map(([cardId]) => seenCards.current[cardId])
        .filter((card): card is Card => Boolean(card)),
    [results],
  );

  return {
    current,
    flipped,
    answered: results.length,
    remaining: queue.length,
    total,
    correct,
    incorrect,
    done: queue.length === 0,
    results,
    missedCards,
    flip,
    answer,
    restart,
  };
}
